import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { 
  ArrowLeft, Wallet, TrendingUp, TrendingDown, Shield, CreditCard,
  Coins, History, Download, Calendar, Filter, RefreshCw,
  ArrowUpRight, ArrowDownLeft, Repeat, PiggyBank, FileText, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PortfolioSummary = () => {
  const { token } = useAuth();
  const { account, bethBalance, availableBalance, pledgedBalance, refreshBalances } = useWeb3();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [portfolioData, setPortfolioData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [dateFilter, setDateFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [ethPrice, setEthPrice] = useState(3500);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchAllPortfolioData();
  }, [token, navigate]);

  const fetchAllPortfolioData = async () => {
    setLoading(true);
    try {
      const [
        transfersRes,
        rewardsRes,
        priceRes,
        portfolioRes
      ] = await Promise.all([
        fetch(`${API}/user/transfers`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/rewards/balance`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/defi/eth-price`),
        fetch(`${API}/user/portfolio-summary`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const transfers = transfersRes.ok ? await transfersRes.json() : { transfers: [] };
      const rewards = rewardsRes.ok ? await rewardsRes.json() : { rewards: {} };
      const price = priceRes.ok ? await priceRes.json() : { price: 3500 };
      const portfolio = portfolioRes.ok ? await portfolioRes.json() : null;

      setEthPrice(price.price || 3500);
      
      // Build comprehensive activity list
      const allActivities = [];
      
      // Add transfers
      (transfers.transfers || []).forEach(t => {
        allActivities.push({
          type: 'transfer_out',
          amount: t.amount,
          to: t.to,
          tx_hash: t.tx_hash,
          date: t.created_at,
          status: 'completed'
        });
      });

      // Add portfolio activities from backend
      if (portfolio?.activities) {
        portfolio.activities.forEach(a => allActivities.push(a));
      }

      // Sort by date
      allActivities.sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setActivities(allActivities);
      setPortfolioData({
        rewards,
        portfolio,
        transfers: transfers.transfers || []
      });
      
    } catch (error) {
      console.error('Error fetching portfolio data:', error);
      toast.error('Failed to load portfolio data');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredActivities = () => {
    let filtered = activities;
    
    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(a => a.type.includes(typeFilter));
    }
    
    // Filter by date
    if (dateFilter !== 'all') {
      const now = new Date();
      const cutoff = new Date();
      
      switch (dateFilter) {
        case 'week':
          cutoff.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoff.setMonth(now.getMonth() - 1);
          break;
        case 'quarter':
          cutoff.setMonth(now.getMonth() - 3);
          break;
        case 'year':
          cutoff.setFullYear(now.getFullYear() - 1);
          break;
        default:
          break;
      }
      
      if (dateFilter !== 'all') {
        filtered = filtered.filter(a => new Date(a.date) >= cutoff);
      }
    }
    
    return filtered;
  };

  const getActivityIcon = (type) => {
    const icons = {
      'transfer_out': <ArrowUpRight className="w-5 h-5 text-red-400" />,
      'transfer_in': <ArrowDownLeft className="w-5 h-5 text-green-400" />,
      'deposit': <ArrowDownLeft className="w-5 h-5 text-blue-400" />,
      'withdrawal': <ArrowUpRight className="w-5 h-5 text-orange-400" />,
      'collateral_pledge': <Shield className="w-5 h-5 text-purple-400" />,
      'collateral_release': <Shield className="w-5 h-5 text-green-400" />,
      'credit_draw': <CreditCard className="w-5 h-5 text-pink-400" />,
      'repayment': <CreditCard className="w-5 h-5 text-emerald-400" />,
      'swap': <Repeat className="w-5 h-5 text-indigo-400" />,
      'reward_claim': <PiggyBank className="w-5 h-5 text-yellow-400" />
    };
    return icons[type] || <Coins className="w-5 h-5 text-gray-400" />;
  };

  const getActivityLabel = (type) => {
    const labels = {
      'transfer_out': 'BETH Transfer (Sent)',
      'transfer_in': 'BETH Transfer (Received)',
      'deposit': 'Deposit',
      'withdrawal': 'Withdrawal',
      'collateral_pledge': 'Collateral Pledged',
      'collateral_release': 'Collateral Released',
      'credit_draw': 'Credit Facility Draw',
      'repayment': 'Credit Repayment',
      'swap': 'Uniswap Swap',
      'reward_claim': 'Reward Claimed'
    };
    return labels[type] || type;
  };

  const calculatePortfolioValue = () => {
    // BETH at $1.00 internal reference price
    const bethValue = bethBalance * 1.0;
    return bethValue;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#b8860b]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="portfolio-summary">
      {/* Header */}
      <header className="border-b border-[#1a2d4a] bg-[#0d1d35]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
            </Link>
            <div className="h-6 w-px bg-[#1a2d4a]" />
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#b8860b]" />
                Portfolio Summary
              </h1>
              <p className="text-xs text-gray-500">Comprehensive view of your financial activities</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAllPortfolioData} className="border-[#1a2d4a]">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Portfolio Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-[#b8860b]/20 to-[#b8860b]/5 border-[#b8860b]/30">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <Wallet className="w-5 h-5 text-[#b8860b]" />
                <p className="text-gray-400 text-sm">Total BETH Holdings</p>
              </div>
              <p className="text-2xl font-bold text-white">{bethBalance.toLocaleString()}</p>
              <p className="text-xs text-[#b8860b] mt-1">≈ ${calculatePortfolioValue().toLocaleString()} USD</p>
            </CardContent>
          </Card>

          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <Shield className="w-5 h-5 text-blue-400" />
                <p className="text-gray-400 text-sm">Available Balance</p>
              </div>
              <p className="text-2xl font-bold text-white">{availableBalance.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">Ready for transfers or pledging</p>
            </CardContent>
          </Card>

          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <CreditCard className="w-5 h-5 text-purple-400" />
                <p className="text-gray-400 text-sm">Pledged as Collateral</p>
              </div>
              <p className="text-2xl font-bold text-white">{pledgedBalance.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">Backing credit facilities</p>
            </CardContent>
          </Card>

          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                <p className="text-gray-400 text-sm">ETH Price</p>
              </div>
              <p className="text-2xl font-bold text-white">${ethPrice.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">Live market price</p>
            </CardContent>
          </Card>
        </div>

        {/* Rewards Summary */}
        {portfolioData?.rewards?.rewards && (
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <PiggyBank className="w-5 h-5 text-yellow-400" />
                Unclaimed Rewards
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-[#1a2d4a]/50 rounded-lg">
                  <p className="text-gray-400 text-sm">LP Rewards</p>
                  <p className="text-xl font-bold text-white">{portfolioData.rewards.rewards.lp_rewards || 0}</p>
                </div>
                <div className="p-4 bg-[#1a2d4a]/50 rounded-lg">
                  <p className="text-gray-400 text-sm">Staking Rewards</p>
                  <p className="text-xl font-bold text-white">{portfolioData.rewards.rewards.staking_rewards || 0}</p>
                </div>
                <div className="p-4 bg-[#1a2d4a]/50 rounded-lg">
                  <p className="text-gray-400 text-sm">Referral Rewards</p>
                  <p className="text-xl font-bold text-white">{portfolioData.rewards.rewards.referral_rewards || 0}</p>
                </div>
                <div className="p-4 bg-[#1a2d4a]/50 rounded-lg">
                  <p className="text-gray-400 text-sm">Platform Incentives</p>
                  <p className="text-xl font-bold text-white">{portfolioData.rewards.rewards.platform_incentives || 0}</p>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <Link to="/rewards">
                  <Button className="bg-[#b8860b] hover:bg-[#9a7209]">
                    Claim Rewards
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={dateFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateFilter('all')}
              className={dateFilter === 'all' ? 'bg-[#b8860b]' : 'border-[#1a2d4a]'}
            >
              All Time
            </Button>
            <Button
              variant={dateFilter === 'week' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateFilter('week')}
              className={dateFilter === 'week' ? 'bg-[#b8860b]' : 'border-[#1a2d4a]'}
            >
              This Week
            </Button>
            <Button
              variant={dateFilter === 'month' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateFilter('month')}
              className={dateFilter === 'month' ? 'bg-[#b8860b]' : 'border-[#1a2d4a]'}
            >
              This Month
            </Button>
            <Button
              variant={dateFilter === 'quarter' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateFilter('quarter')}
              className={dateFilter === 'quarter' ? 'bg-[#b8860b]' : 'border-[#1a2d4a]'}
            >
              Last 3 Months
            </Button>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={typeFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTypeFilter('all')}
              className={typeFilter === 'all' ? 'bg-[#b8860b]' : 'border-[#1a2d4a]'}
            >
              <Filter className="w-4 h-4 mr-1" />
              All Types
            </Button>
            <Button
              variant={typeFilter === 'transfer' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTypeFilter('transfer')}
              className={typeFilter === 'transfer' ? 'bg-blue-600' : 'border-[#1a2d4a]'}
            >
              Transfers
            </Button>
            <Button
              variant={typeFilter === 'collateral' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTypeFilter('collateral')}
              className={typeFilter === 'collateral' ? 'bg-purple-600' : 'border-[#1a2d4a]'}
            >
              Collateral
            </Button>
            <Button
              variant={typeFilter === 'credit' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTypeFilter('credit')}
              className={typeFilter === 'credit' ? 'bg-pink-600' : 'border-[#1a2d4a]'}
            >
              Credit
            </Button>
          </div>
        </div>

        {/* Activity History */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <History className="w-5 h-5 text-[#b8860b]" />
              Activity History
            </CardTitle>
            <CardDescription>
              Complete record of all your platform activities
            </CardDescription>
          </CardHeader>
          <CardContent>
            {getFilteredActivities().length === 0 ? (
              <div className="text-center py-12">
                <History className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No activities found</p>
                <p className="text-gray-500 text-sm mt-2">
                  Your transactions, collateral deposits, and credit activities will appear here
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {getFilteredActivities().map((activity, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-4 bg-[#1a2d4a]/50 rounded-lg hover:bg-[#1a2d4a] transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#0a1628] flex items-center justify-center">
                        {getActivityIcon(activity.type)}
                      </div>
                      <div>
                        <p className="text-white font-medium">{getActivityLabel(activity.type)}</p>
                        <p className="text-xs text-gray-500">{formatDate(activity.date)}</p>
                        {activity.to && (
                          <p className="text-xs text-gray-600 font-mono">
                            To: {activity.to.slice(0, 8)}...{activity.to.slice(-6)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-medium ${
                        activity.type.includes('out') || activity.type.includes('draw') || activity.type.includes('pledge')
                          ? 'text-red-400'
                          : 'text-green-400'
                      }`}>
                        {activity.type.includes('out') || activity.type.includes('draw') || activity.type.includes('pledge') ? '-' : '+'}
                        {activity.amount?.toLocaleString()} BETH
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        activity.status === 'completed' 
                          ? 'bg-green-900/30 text-green-400' 
                          : 'bg-yellow-900/30 text-yellow-400'
                      }`}>
                        {activity.status || 'completed'}
                      </span>
                      {activity.tx_hash && (
                        <a 
                          href={`https://etherscan.io/tx/${activity.tx_hash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block mt-1 text-xs text-[#b8860b] hover:underline"
                        >
                          View TX <ExternalLink className="w-3 h-3 inline" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a]">
          <CardContent className="py-6">
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/transfer">
                <Button variant="outline" className="border-[#1a2d4a]">
                  <ArrowUpRight className="w-4 h-4 mr-2" />
                  Transfer BETH
                </Button>
              </Link>
              <Link to="/defi">
                <Button variant="outline" className="border-[#1a2d4a]">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Credit Facility
                </Button>
              </Link>
              <Link to="/credit-enhancement">
                <Button variant="outline" className="border-[#1a2d4a]">
                  <Shield className="w-4 h-4 mr-2" />
                  Credit Enhancement
                </Button>
              </Link>
              <Link to="/my-transactions">
                <Button variant="outline" className="border-[#1a2d4a]">
                  <History className="w-4 h-4 mr-2" />
                  Transaction Details
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Disclaimer */}
        <div className="p-4 bg-[#0d1d35] rounded-lg border border-[#1a2d4a]">
          <p className="text-xs text-gray-500 text-center">
            <strong className="text-gray-400">Portfolio Disclaimer:</strong> Portfolio values shown are indicative 
            and based on internal reference pricing. BETH token value is $1.00 USD as per treasury policy. 
            Actual market value may vary. This is not financial advice.
          </p>
        </div>
      </main>
    </div>
  );
};

export default PortfolioSummary;
